import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import { TUTORIAL_VERSION } from '../../data/tutorials';
import { useAuth } from '../../lib/auth';

const TutorialContext = createContext(null);

const TUTORIAL_STORAGE_KEY = 'austuition.tutorials.completed';
const getAccountStorageKey = (userId) => `${TUTORIAL_STORAGE_KEY}.${userId}`;

export function TutorialProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const [completedTutorials, setCompletedTutorials] = useState(new Set());
  const [isProgressReady, setIsProgressReady] = useState(false);
  const [isManualRestart, setIsManualRestart] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const userId = user?.user_id ? String(user.user_id) : null;

  // Hydrate only after the signed-in account is known. This prevents the
  // dashboard from briefly treating an existing account as a first-time user.
  useEffect(() => {
    if (authLoading) {
      setIsProgressReady(false);
      return;
    }

    if (!userId) {
      setCompletedTutorials(new Set());
      setIsProgressReady(true);
      return;
    }

    setIsProgressReady(false);
    try {
      const completed = new Set(
        Array.isArray(user?.completed_tutorials) ? user.completed_tutorials : [],
      );
      if (user?.tutor_dashboard_tutorial_completed) {
        completed.add('tutor-dashboard');
      }

      const accountKey = getAccountStorageKey(userId);
      const accountStored = window.localStorage.getItem(accountKey);
      if (accountStored) {
        const data = JSON.parse(accountStored);
        if (Array.isArray(data.completedTutorials)) {
          data.completedTutorials.forEach((tutorialId) => completed.add(tutorialId));
        }
      } else {
        // One-time migration from the old shared key. Never import another
        // account's progress into the currently signed-in account.
        const legacyStored = window.localStorage.getItem(TUTORIAL_STORAGE_KEY);
        if (legacyStored) {
          const legacyData = JSON.parse(legacyStored);
          if (
            String(legacyData.userId || '') === userId
            && Array.isArray(legacyData.completedTutorials)
          ) {
            legacyData.completedTutorials.forEach((tutorialId) => completed.add(tutorialId));
          }
        }
      }

      const data = {
        userId,
        role: user?.role || 'unknown',
        completedTutorials: Array.from(completed),
        version: TUTORIAL_VERSION,
      };
      window.localStorage.setItem(accountKey, JSON.stringify(data));
      setCompletedTutorials(completed);
    } catch (error) {
      console.error('Failed to load tutorial progress:', error);
      setCompletedTutorials(new Set(
        Array.isArray(user?.completed_tutorials) ? user.completed_tutorials : [],
      ));
    } finally {
      setIsProgressReady(true);
    }
  }, [
    authLoading,
    userId,
    user?.role,
    user?.completed_tutorials,
    user?.tutor_dashboard_tutorial_completed,
  ]);

  const saveProgress = useCallback((tutorialId, progressUserId, role) => {
    const accountUserId = progressUserId ? String(progressUserId) : userId;
    if (!tutorialId || !accountUserId) return;

    try {
      setCompletedTutorials((previous) => {
        const next = new Set(previous);
        next.add(tutorialId);
        const data = {
          userId: accountUserId,
          role: role || user?.role || 'unknown',
          completedTutorials: Array.from(next),
          lastDismissed: new Date().toISOString(),
          version: TUTORIAL_VERSION,
        };
        window.localStorage.setItem(getAccountStorageKey(accountUserId), JSON.stringify(data));
        return next;
      });
    } catch (error) {
      console.error('Failed to save tutorial progress:', error);
    }
  }, [userId, user?.role]);

  const startTutorial = useCallback((tutorialId, isManual = false, options = {}) => {
    setCurrentTutorial(tutorialId);
    setCurrentStep(0);
    setIsActive(true);
    setIsManualRestart(isManual);
    setIsRequired(Boolean(options?.required) && !isManual);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  const exitTutorial = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    setCurrentTutorial(null);
    setIsManualRestart(false);
    setIsRequired(false);
  }, []);

  const skipTutorial = useCallback((userId, role) => {
    if (isRequired) return;
    if (currentTutorial) {
      saveProgress(currentTutorial, userId, role);
    }
    exitTutorial();
  }, [currentTutorial, isRequired, saveProgress, exitTutorial]);

  const completeTutorial = useCallback((userId, role) => {
    if (currentTutorial) {
      saveProgress(currentTutorial, userId, role);
    }
    exitTutorial();
  }, [currentTutorial, saveProgress, exitTutorial]);

  const shouldShowAutoTutorial = useCallback((tutorialId) => {
    return isProgressReady && !completedTutorials.has(tutorialId);
  }, [completedTutorials, isProgressReady]);

  const resetTutorials = useCallback(() => {
    try {
      if (userId) {
        window.localStorage.removeItem(getAccountStorageKey(userId));
      }
      setCompletedTutorials(new Set());
    } catch (error) {
      console.error('Failed to reset tutorials:', error);
    }
  }, [userId]);

  const value = useMemo(() => ({
    isActive,
    currentStep,
    currentTutorial,
    completedTutorials,
    isProgressReady,
    isManualRestart,
    isRequired,
    startTutorial,
    nextStep,
    prevStep,
    exitTutorial,
    skipTutorial,
    completeTutorial,
    shouldShowAutoTutorial,
    resetTutorials,
  }), [
    isActive,
    currentStep,
    currentTutorial,
    completedTutorials,
    isProgressReady,
    isManualRestart,
    isRequired,
    startTutorial,
    nextStep,
    prevStep,
    exitTutorial,
    skipTutorial,
    completeTutorial,
    shouldShowAutoTutorial,
    resetTutorials,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export { TutorialContext };
